import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { BaseModal } from '../ui/BaseModal';
import { DiffuseGroupsList } from './DiffuseGroupsList';
import { DueDateField } from './DueDateField';
import { useGetCompanyGroups } from '../../services/groups.service';
import { useDiffuseDocument } from '../../services/documents.service';
import { queryClient } from '../../lib/queryClient';
import { UserCompanyRole, isRoleAtLeast } from '@monorepo/shared';

interface DiffuseDocumentModalProps {
  companyId: string;
  currentUserRole?: UserCompanyRole;
}

export interface DiffuseDocumentModalRef {
  open: (documentId: string, documentName: string) => void;
}

export const DiffuseDocumentModal = forwardRef<DiffuseDocumentModalRef, DiffuseDocumentModalProps>(
  ({ companyId, currentUserRole }, ref) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [documentId, setDocumentId] = useState<string | null>(null);
    const [documentName, setDocumentName] = useState('');
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [dueDate, setDueDate] = useState('');

    const { data: groupsData, isLoading: isLoadingGroups } = useGetCompanyGroups(companyId, 1, 100);
    const diffuseDocumentMutation = useDiffuseDocument(companyId);

    const groups = groupsData?.items ?? [];

    useImperativeHandle(ref, () => ({
      open: (docId: string, docName: string) => {
        setDocumentId(docId);
        setDocumentName(docName);
        setSelectedGroupIds([]);
        setDueDate('');
        setIsOpen(true);
      },
    }));

    useEffect(() => {
      if (!isOpen) {
        setSelectedGroupIds([]);
        setDueDate('');
        setDocumentId(null);
        setDocumentName('');
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleFocus = () => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            Array.isArray(query.queryKey) &&
            query.queryKey[0] === 'company-groups' &&
            query.queryKey[1] === companyId,
        });
      };

      window.addEventListener('focus', handleFocus);
      return () => window.removeEventListener('focus', handleFocus);
    }, [isOpen, companyId]);

    const handleOpenGroup = (groupId: string) => {
      window.open(`/admin/groups/${groupId}`, '_blank', 'noopener,noreferrer');
    };

    const handleGroupToggle = (groupId: string) => {
      setSelectedGroupIds((prev) =>
        prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
      );
    };

    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if (!documentId || selectedGroupIds.length === 0) return;

      diffuseDocumentMutation.mutate(
        {
          documentId,
          groupIds: selectedGroupIds,
          ...(dueDate ? { dueDate } : {}),
        },
        { onSuccess: handleClose },
      );
    };

    const handleClose = () => {
      setIsOpen(false);
    };

    const isLoading = diffuseDocumentMutation.isPending;
    const canSubmit = selectedGroupIds.length > 0 && !isLoading;
    const canManageGroups = currentUserRole
      ? isRoleAtLeast(currentUserRole, UserCompanyRole.SUPER_USER)
      : false;

    const footer = (
      <div className="flex gap-4">
        <button
          type="button"
          onClick={handleClose}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition"
          disabled={isLoading}
        >
          {t('documents.diffuse_modal.cancel')}
        </button>
        <button
          type="submit"
          form="diffuse-document-form"
          className="flex-1 px-4 py-2 bg-primary text-white rounded-md hover:bg-opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2"
          disabled={!canSubmit}
        >
          {isLoading && <CircleNotchIcon size={16} className="animate-spin" weight="bold" />}
          {t('documents.diffuse_modal.submit')}
        </button>
      </div>
    );

    return (
      <BaseModal
        isOpen={isOpen}
        onClose={handleClose}
        title={t('documents.diffuse_modal.title')}
        footer={footer}
        maxWidth="md"
      >
        <form
          id="diffuse-document-form"
          onSubmit={handleSubmit}
          className="flex flex-1 min-h-0 flex-col gap-6"
        >
          <div className="flex-shrink-0">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('documents.diffuse_modal.document_name')}
            </label>
            <p className="text-gray-900 font-medium">{documentName}</p>
          </div>

          <DueDateField
            id="diffuse-document-due-date"
            label={t('documents.diffuse_modal.due_date')}
            value={dueDate}
            onChange={setDueDate}
          />

          <DiffuseGroupsList
            groups={groups}
            isLoadingGroups={isLoadingGroups}
            selectedGroupIds={selectedGroupIds}
            canManageGroups={canManageGroups}
            onToggle={handleGroupToggle}
            onOpenGroup={handleOpenGroup}
            onCreateGroups={() => {
              handleClose();
              navigate('/admin/users');
            }}
          />

          {selectedGroupIds.length > 0 && (
            <p className="flex-shrink-0 text-sm text-gray-600">
              {t('documents.diffuse_modal.groups.selected', { count: selectedGroupIds.length })}
            </p>
          )}
        </form>
      </BaseModal>
    );
  },
);

DiffuseDocumentModal.displayName = 'DiffuseDocumentModal';
